import { useState, useEffect, useRef } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import JsonLd from '../../components/base/JsonLd';

function useCountUp(target: number, duration: number = 2000, start: boolean = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);
  return count;
}

export default function Home() {
  const [statsVisible, setStatsVisible] = useState(false);
  const [heroVisible, setHeroVisible] = useState(false);
  const statsRef = useRef<HTMLDivElement>(null);

  const patients = useCountUp(250, 2200, statsVisible);
  const staff = useCountUp(40, 2200, statsVisible);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://celebrity-homehealth.com';

  const homeJsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      '@id': `${siteUrl}/#website`,
      url: siteUrl,
      name: 'Celebrity Home Health',
      description: 'Medicare certified and ACHC accredited home health care agency serving Las Vegas, NV.',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'HomeHealthService',
      '@id': `${siteUrl}/#organization`,
      name: 'Celebrity Home Health',
      alternateName: 'Celebrity Home Health, Inc.',
      url: siteUrl,
      logo: `${siteUrl}/logo.png`,
      telephone: '+18669390330',
      email: 'info@celebrity-homehealth.com',
      foundingDate: '2023',
      description: 'Medicare certified and ACHC accredited home health care agency providing skilled nursing, physical therapy, occupational therapy, speech therapy, medical social services, and home health aide in Las Vegas, Nevada.',
      hasCredential: ['Medicare Certified', 'ACHC Accredited'],
      founder: {
        '@type': 'Person',
        name: 'Dr. Osmel Villarreal, DBA, MBA, MS',
        jobTitle: 'Administrator',
      },
      address: {
        '@type': 'PostalAddress',
        streetAddress: '2121 E Flamingo Rd Ste 218A',
        addressLocality: 'Las Vegas',
        addressRegion: 'NV',
        postalCode: '89119',
        addressCountry: 'US',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: '36.1037',
        longitude: '-115.1481',
      },
      openingHoursSpecification: [
        { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'], opens: '08:00', closes: '17:00' },
        { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Saturday'], opens: '09:00', closes: '14:00' },
      ],
      areaServed: ['Las Vegas', 'Henderson', 'North Las Vegas', 'Summerlin', 'Green Valley', 'Enterprise', 'Spring Valley', 'Paradise'],
      serviceType: ['Skilled Nursing', 'Physical Therapy', 'Occupational Therapy', 'Speech Therapy', 'Medical Social Services', 'Home Health Aide'],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${siteUrl}/#webpage`,
      url: siteUrl,
      name: 'Celebrity Home Health Las Vegas | Medicare Home Care',
      description: 'Celebrity Home Health delivers Medicare certified, ACHC accredited home health care in Las Vegas NV. Skilled nursing, physical therapy, occupational therapy, speech therapy & home health aide services.',
      isPartOf: { '@id': `${siteUrl}/#website` },
      about: { '@id': `${siteUrl}/#organization` },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [{ '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl }],
      },
    },
  ];

  useEffect(() => {
    setTimeout(() => setHeroVisible(true), 100);
    document.title = 'Celebrity Home Health Las Vegas | Medicare Home Care';
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStatsVisible(true); },
      { threshold: 0.3 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  const services = [
    { icon: 'ri-nurse-line', title: 'Skilled Nursing', desc: 'Expert RN & LPN care including wound care, IV therapy, medication management, and vital sign monitoring.', href: '/services/skilled-nursing' },
    { icon: 'ri-walk-line', title: 'Physical Therapy', desc: 'Personalized rehab to improve mobility, strength, and balance after injury or surgery.', href: '/services/physical-therapy' },
    { icon: 'ri-hand-heart-line', title: 'Occupational Therapy', desc: 'Daily living assistance, adaptive equipment training, and home safety assessments.', href: '/services/occupational-therapy' },
    { icon: 'ri-chat-voice-line', title: 'Speech Therapy', desc: 'Treatment for communication, language, and swallowing disorders.', href: '/services/speech-therapy' },
    { icon: 'ri-heart-pulse-line', title: 'Medical Social Services', desc: 'Counseling and support for emotional, social, and financial challenges related to illness.', href: '/services/medical-social-services' },
    { icon: 'ri-user-heart-line', title: 'Home Health Aide', desc: 'Compassionate personal care, bathing, dressing, grooming, and light housekeeping.', href: '/services/home-health-aide' },
  ];

  const reasons = [
    { icon: 'ri-shield-check-line', title: 'Medicare Certified', desc: 'Medicare approved in 2025, enabling us to serve more patients across the Las Vegas Valley.' },
    { icon: 'ri-award-line', title: 'ACHC Accredited', desc: 'Accredited by the Accreditation Commission for Health Care, reflecting our commitment to the highest standards.' },
    { icon: 'ri-user-heart-line', title: 'Personalized Care Plans', desc: 'Custom treatment plans designed for your unique health needs and goals.' },
    { icon: 'ri-time-line', title: '24/7 On-Call Support', desc: 'Round-the-clock availability for emergencies and urgent care needs.' },
    { icon: 'ri-map-pin-2-line', title: 'Serving All of Las Vegas', desc: 'Covering Henderson, Summerlin, North Las Vegas, and surrounding areas.' },
    { icon: 'ri-sparkling-line', title: 'Est. 2023', desc: 'A modern, growing home health agency built on a foundation of compassionate, high-quality care.' },
  ];

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
      <JsonLd data={homeJsonLd as Record<string, unknown>[]} id="json-ld-home" />
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/902b68bed079b380a901a5db2e8c1724.jpg"
            alt="Celebrity Home Health - Professional Home Care"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-900/70 to-slate-900/30"></div>
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-12 pt-24 pb-20">
          <div className="max-w-2xl">
            <div
              className={`transition-all duration-1000 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/20 border border-amber-400/30 rounded-full text-amber-300 text-sm font-medium mb-8">
                <i className="ri-map-pin-2-line"></i>
                Las Vegas, Nevada · Medicare Certified · ACHC Accredited
              </div>

              <h1 className="text-5xl lg:text-7xl font-bold text-white leading-[1.05] mb-6">
                Where Every<br />
                Client is a{' '}
                <span className="text-gradient" style={{
                  background: 'linear-gradient(135deg, #f59e0b, #fcd34d)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}>Star</span>
              </h1>

              <p className="text-lg text-slate-300 leading-relaxed mb-10 max-w-xl">
                Professional home health care delivered with compassion and clinical excellence. Medicare certified and ACHC accredited, serving Las Vegas families with skilled nursing, therapy, and personal care.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="/contact"
                  className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-amber-600 hover:bg-amber-500 text-white rounded-xl font-semibold text-base transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-calendar-check-line"></i>
                  Request Care Today
                </a>
                <a
                  href="tel:866-939-0330"
                  className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-white/10 hover:bg-white/20 border border-white/20 text-white rounded-xl font-semibold text-base transition-all duration-300 whitespace-nowrap cursor-pointer backdrop-blur-sm"
                >
                  <i className="ri-phone-line"></i>
                  866-939-0330
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Floating badge */}
        <div className="absolute bottom-10 right-10 hidden lg:block">
          <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-5 text-white">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center">
                <i className="ri-shield-check-fill text-amber-400 text-2xl"></i>
              </div>
              <div>
                <div className="text-xs text-slate-300 mb-0.5">Est. 2023 · Las Vegas, NV</div>
                <div className="font-bold text-sm">Medicare Certified · ACHC Accredited</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <div ref={statsRef} className="bg-slate-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
          <div className="grid grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-4xl lg:text-5xl font-bold text-amber-400 mb-2">{patients.toLocaleString()}+</div>
              <div className="text-slate-400 text-sm font-medium uppercase tracking-wider">Patients Served</div>
            </div>
            <div className="border-x border-slate-800">
              <div className="text-4xl lg:text-5xl font-bold text-amber-400 mb-2">{staff}+</div>
              <div className="text-slate-400 text-sm font-medium uppercase tracking-wider">Healthcare Professionals</div>
            </div>
            <div>
              <div className="text-4xl lg:text-5xl font-bold text-amber-400 mb-2">ACHC</div>
              <div className="text-slate-400 text-sm font-medium uppercase tracking-wider">Accredited Agency</div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-3 gap-6">
            <a href="/contact" className="group p-8 bg-white rounded-2xl border border-stone-200 hover:border-amber-300 hover:-translate-y-1 transition-all duration-300 cursor-pointer">
              <div className="w-13 h-13 w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mb-5 group-hover:bg-amber-200 transition-colors duration-300">
                <i className="ri-calendar-check-line text-amber-700 text-xl"></i>
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Request Care</h3>
              <p className="text-slate-500 text-sm mb-4">Start your journey to better health at home with a free consultation.</p>
              <span className="text-amber-600 text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all duration-200">
                Get Started <i className="ri-arrow-right-line"></i>
              </span>
            </a>

            <a href="tel:866-939-0330" className="group p-8 bg-amber-600 hover:bg-amber-700 rounded-2xl transition-all duration-300 cursor-pointer hover:-translate-y-1">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-5">
                <i className="ri-phone-line text-white text-xl"></i>
              </div>
              <h3 className="text-lg font-bold text-white mb-2">24/7 Support</h3>
              <p className="text-amber-100 text-sm mb-4">Call anytime for immediate assistance or to speak with a care coordinator.</p>
              <span className="text-white text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all duration-200">
                866-939-0330 <i className="ri-arrow-right-line"></i>
              </span>
            </a>

            <a href="/careers" className="group p-8 bg-white rounded-2xl border border-stone-200 hover:border-amber-300 hover:-translate-y-1 transition-all duration-300 cursor-pointer">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mb-5 group-hover:bg-amber-200 transition-colors duration-300">
                <i className="ri-briefcase-line text-amber-700 text-xl"></i>
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">Join Our Team</h3>
              <p className="text-slate-500 text-sm mb-4">Explore rewarding career opportunities with a top-rated home health agency.</p>
              <span className="text-amber-600 text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all duration-200">
                View Openings <i className="ri-arrow-right-line"></i>
              </span>
            </a>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <div className="inline-block px-3 py-1.5 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold uppercase tracking-wider mb-4">
              What We Offer
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-5">
              Comprehensive Home<br />Health Care Services
            </h2>
            <p className="text-slate-500 text-lg max-w-2xl mx-auto">
              Professional medical care tailored to your unique needs, delivered in the comfort and privacy of your home.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, i) => (
              <a
                key={i}
                href={service.href}
                className="group p-8 rounded-2xl border border-stone-200 hover:border-amber-300 hover:bg-amber-50/50 transition-all duration-300 block cursor-pointer"
              >
                <div className="w-12 h-12 bg-slate-900 group-hover:bg-amber-600 rounded-xl flex items-center justify-center mb-6 transition-colors duration-300">
                  <i className={`${service.icon} text-white text-xl`}></i>
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-3">{service.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed mb-5">{service.desc}</p>
                <span className="text-amber-600 text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all duration-200">
                  Learn More <i className="ri-arrow-right-line"></i>
                </span>
              </a>
            ))}
          </div>

          <div className="text-center mt-12">
            <a href="/services" className="inline-flex items-center gap-2 px-7 py-3.5 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer">
              View All Services <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-block px-3 py-1.5 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold uppercase tracking-wider mb-5">
                Why Choose Us
              </div>
              <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6 leading-tight">
                The Celebrity Home Health Difference
              </h2>
              <p className="text-slate-500 text-lg mb-10">
                We're committed to providing exceptional care that helps you heal and thrive in the comfort of your own home.
              </p>

              <div className="grid sm:grid-cols-2 gap-5">
                {reasons.map((r, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className={`${r.icon} text-amber-700 text-lg`}></i>
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900 text-sm mb-1">{r.title}</h3>
                      <p className="text-slate-500 text-xs leading-relaxed">{r.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="relative w-full h-[560px] rounded-3xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=professional-diverse-home-health-care-team-of-three-nurses-smiling-warmly-standing-together-outdoors-in-bright-natural-daylight-white-medical-uniforms-confident-and-caring-expressions-clean-simple-outdoor-background-suburban-neighborhood-healthcare-workers-group-portrait-2025-modern&width=700&height=560&seq=why-choose-team-2026&orientation=portrait"
                  alt="Celebrity Home Health Team"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-transparent to-transparent"></div>
              </div>

              {/* Floating card */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-5 border border-stone-200 max-w-[220px]">
                <div className="flex items-center gap-2 mb-3">
                  {[1,2,3,4,5].map((s) => (
                    <i key={s} className="ri-star-fill text-amber-500 text-sm"></i>
                  ))}
                </div>
                <p className="text-slate-700 text-sm font-medium leading-snug">"Truly exceptional care. They treated my mother like family."</p>
                <p className="text-slate-400 text-xs mt-2">— Maria T., Las Vegas</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Specialty Programs */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <div className="inline-block px-3 py-1.5 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold uppercase tracking-wider mb-4">
              Specialty Care
            </div>
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Specialized Care Programs</h2>
            <p className="text-slate-500 text-lg max-w-2xl mx-auto">Tailored programs for specific health conditions and recovery needs</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                href: '/services/chronic-disease-management',
                icon: 'ri-heart-pulse-line',
                title: 'Chronic Disease Management',
                desc: 'Comprehensive care for CHF, COPD, diabetes, hypertension, and other chronic conditions.',
                img: 'https://readdy.ai/api/search-image?query=nurse-checking-blood-pressure-of-elderly-patient-in-bright-clean-home-interior-warm-and-professional-care-scene-soft-daylight&width=400&height=220&seq=specialty1-2026&orientation=landscape',
              },
              {
                href: '/services/post-surgical-care',
                icon: 'ri-surgical-mask-line',
                title: 'Post-Surgical Care',
                desc: 'Expert recovery support after surgery to ensure optimal healing and prevent complications.',
                img: 'https://readdy.ai/api/search-image?query=physical-therapist-assisting-elderly-man-with-knee-replacement-recovery-exercises-in-clean-modern-home-living-room-bright-natural-light&width=400&height=220&seq=specialty2-2026&orientation=landscape',
              },
              {
                href: '/services/transitional-care',
                icon: 'ri-hospital-line',
                title: 'Transitional Care',
                desc: 'Smooth, safe transition from hospital to home with coordinated care and 24/7 support.',
                img: 'https://readdy.ai/api/search-image?query=caring-nurse-reviewing-discharge-papers-with-elderly-patient-at-comfortable-home-bright-white-room-professional-healthcare-setting&width=400&height=220&seq=specialty3-2026&orientation=landscape',
              },
            ].map((item, i) => (
              <a key={i} href={item.href} className="group block rounded-2xl overflow-hidden border border-stone-200 hover:border-amber-300 hover:-translate-y-1 transition-all duration-300 cursor-pointer">
                <div className="w-full h-48 overflow-hidden">
                  <img src={item.img} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-7">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center mb-4">
                    <i className={`${item.icon} text-amber-700 text-lg`}></i>
                  </div>
                  <h3 className="font-bold text-slate-900 text-lg mb-2">{item.title}</h3>
                  <p className="text-slate-500 text-sm mb-4">{item.desc}</p>
                  <span className="text-amber-600 text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all duration-200">
                    Learn More <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-slate-900 relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=soft-blurred-warm-interior-home-background-cozy-living-room-natural-light-bokeh-effect-abstract-home-atmosphere&width=1440&height=500&seq=cta-bg-2026&orientation=landscape"
            alt=""
            className="w-full h-full object-cover opacity-10"
          />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/20 border border-amber-400/30 rounded-full text-amber-300 text-sm font-medium mb-8">
            <i className="ri-sparkling-line"></i>
            Ready to Get Started?
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
            Quality Care Starts<br />Right at Your Door
          </h2>
          <p className="text-slate-400 text-lg mb-10 max-w-2xl mx-auto">
            Contact us today to learn how Celebrity Home Health can help you or your loved one receive exceptional care in the comfort of home.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href="/contact" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-amber-600 hover:bg-amber-500 text-white rounded-xl font-semibold text-base transition-all duration-300 whitespace-nowrap cursor-pointer">
              <i className="ri-calendar-check-line"></i>
              Request Care Now
            </a>
            <a href="tel:866-939-0330" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 hover:bg-white/15 border border-white/20 text-white rounded-xl font-semibold text-base transition-all duration-300 whitespace-nowrap cursor-pointer">
              <i className="ri-phone-line"></i>
              866-939-0330
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
